import { useRef, useEffect, useCallback } from "react";

export const usePortfolioUpdates = ({
  calculatePositions,
  fundVal,
  StrategeyVal,
  StrategeyTypename,
  setFilteredValues,
  setTotals,
}) => {
  const updateTimeoutRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  const UPDATE_INTERVAL = 2 * 60 * 1000; // 2 minutes in milliseconds

  // Debounced update function
  const debouncedUpdate = useCallback(() => {
    const now = Date.now();

    // Clear existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // If enough time has passed since last update, update immediately
    if (now - lastUpdateTimeRef.current >= UPDATE_INTERVAL) {
      calculatePositions(fundVal, StrategeyVal, StrategeyTypename, setFilteredValues, setTotals);
      lastUpdateTimeRef.current = now;
    } else {
      // Otherwise, schedule an update for the remaining time
      const timeUntilNextUpdate = UPDATE_INTERVAL - (now - lastUpdateTimeRef.current);
      updateTimeoutRef.current = setTimeout(() => {
        calculatePositions(fundVal, StrategeyVal, StrategeyTypename, setFilteredValues, setTotals);
        lastUpdateTimeRef.current = Date.now();
      }, timeUntilNextUpdate);
    }
  }, [calculatePositions, fundVal, StrategeyVal, StrategeyTypename, setFilteredValues, setTotals]);

  // Effect to handle market data updates with debouncing
  useEffect(() => {
    // Always calculate immediately for now to fix loader issue
    calculatePositions(fundVal, StrategeyVal, StrategeyTypename, setFilteredValues, setTotals);
    lastUpdateTimeRef.current = Date.now();

    // Cleanup timeout on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [calculatePositions, fundVal, StrategeyVal, StrategeyTypename, setFilteredValues, setTotals]);

  // Effect to handle filter changes (immediate update)
  useEffect(() => {
    // For filter changes, update immediately and reset the timer
    calculatePositions(fundVal, StrategeyVal, StrategeyTypename, setFilteredValues, setTotals);
    lastUpdateTimeRef.current = Date.now();
  }, [fundVal, StrategeyVal, StrategeyTypename, calculatePositions, setFilteredValues, setTotals]);

  return {
    debouncedUpdate,
  };
};
